import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { popModal } from '@/redux/modals/actions';

type ModalUnsavedChangesProps = {
  // Optional "Save" primary (save, then leave); when omitted the primary is "Go back".
  onSave?: () => void | Promise<void>;
  onDiscard: () => void;
  onCancel: () => void;
};

const ModalUnsavedChanges = ({ onSave, onDiscard, onCancel }: ModalUnsavedChangesProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);

  const close = () => {
    dispatch(popModal());
  };

  const handleSave = async () => {
    if (!onSave) {
      return;
    }
    setIsSaving(true);
    try {
      await onSave();
      close();
    } catch (error) {
      if (error instanceof Error && error.name === 'FormValidationError') {
        onCancel();
        close();
        return;
      }
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    onDiscard();
    close();
  };

  const handleCancel = () => {
    if (isSaving) {
      return;
    }
    onCancel();
    close();
  };

  return (
    <Dialog open={true} onOpenChange={handleCancel}>
      <DialogContent className="gap-8 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl tracking-tight">
            {t('ai_agents.unsaved_changes.title')}
          </DialogTitle>
          <DialogDescription className="text-base leading-6">
            {t('prompts.leave_warning')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost-neutral" onClick={handleDiscard} disabled={isSaving}>
            {t('prompts.unsaved_changes.discard_changes')}
          </Button>
          {onSave ? (
            <Button onClick={handleSave} isLoading={isSaving}>
              {t('common.save')}
            </Button>
          ) : (
            <Button onClick={handleCancel}>{t('common.go_back')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalUnsavedChanges;
